import { CopyOutlined, DeleteOutlined, FormOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Input, Modal, Select, Table } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AdGroupBean, AdvertiserBean, CampaignBean, ItemResponseAdGroupBeanObject, ListPagination } from 'src/api/api'
import adGroupApi from 'src/component/app/adGroup/AdGroupApi'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import { StorageKey, memoryStorage, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { useDebounce } from 'src/util/useDebounce'
import { toSort, useSorter } from 'src/util/useSorter'
import { capitalizedView, daysToEnd, formatLocale } from 'src/util/Util'

const Column = Table.Column
const Option = Select.Option

const storageWriter = storageWriterFactory('adGroup', memoryStorage)
const storageReader = storageReaderFactory('adGroup', memoryStorage)

const now = dayjs()

const formInfoItems: FormDataType[] = [
    FormDataType.Campaigns
]

const AdGroupList = () => {

    const [data, setData] = useState<AdGroupBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [info, setInfo] = useState({
        campaigns: []
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const savedParams = storageReader.read(StorageKey.SearchParams)
    const isInitialLoad = useRef(true)

    const [campaign, setCampaign] = useState<number | undefined>(savedParams.campaign ?? undefined)
    const [status, setStatus] = useState<AdGroupBean.StatusEnum | undefined>(savedParams.status ?? undefined)
    const [error, setError] = useState()

    const loadData = async (page: number) => {
        const response = await adGroupApi.list(pagination.defaultPageSize, page, toSort(sorter), search, campaign, status)

        const array = response.data as Array<ItemResponseAdGroupBeanObject>
        const adGroupBeans: AdGroupBean[] = array.map(item => item.data as AdGroupBean)

        if (adGroupBeans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage,
                campaign,
                status
            })

            setData(adGroupBeans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const handleCampaignChange = (value: number | undefined) => {
        setCampaign(value)
        storageWriter.write(StorageKey.SearchParams, { campaign: value ?? null })
    }

    const handleStatusChange = (value: AdGroupBean.StatusEnum | undefined) => {
        setStatus(value)
        storageWriter.write(StorageKey.SearchParams, { status: value ?? null })
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    const loadInfo = async () => {
        const resp = await formDataLoader(...formInfoItems)
        setInfo(resp as any)
    }

    useEffect(() => {
        loadInfo()
    }, [])

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false
            loadData(savedParams.page || pagination.current)
        } else {
            loadData(1)
        }
    }, [debouncedSearch, campaign, status, sorter])

    const changeStatus = async (record: AdGroupBean, newStatus: AdGroupBean.StatusEnum) => {
        try {
            await adGroupApi.statusChange(record.id, newStatus)
            setData(prev => prev.map(item => item.id === record.id ? { ...item, status: newStatus } : item))
        } catch (e) {
            setError(e.error)
        }
    }

    const remove = (record: AdGroupBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                try {
                    await adGroupApi.delete(record.id as number)

                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }

                    loadData(page)
                } catch (e) {
                    setError(e.error)
                }
            }
        })
    }

    return <Card
        title="Ad Group"
        className="list-card"
        extra={<Link to={`/ad-group/create`} className="create-new-link">
            <Button htmlType="button" type="primary" icon={<PlusOutlined />}>
                New Ad Group
            </Button>
        </Link>}
    >
        {error && <Alert message={error} type="error" />}
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name" onChange={e => setSearch(e.target.value || undefined)} />
            <Select placeholder="Filter by Campaign" value={campaign} onChange={handleCampaignChange} allowClear>
                {info.campaigns.map((item: AdvertiserBean) => <Option key={item.id} value={item.id}>{item.name}</Option>)}
            </Select>
            <Select placeholder="Filter by Status" value={status} onChange={handleStatusChange} allowClear>
                {Object.values(CampaignBean.StatusEnum).map(type => <Option key={type} value={type}>{capitalizedView(type)}</Option>)}
            </Select>
        </div>
        <Table
            rowKey="id"
            size="middle"
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column
                sorter
                key="id"
                title="ID"
                dataIndex="id"
            />
            <Column
                sorter
                key="name"
                title="Name"
                dataIndex="name"
                render={(text, record: AdGroupBean) => <Link to={`/ad-group/${record.id}/edit`}>{text}</Link>}
            />
            <Column
                key="campaignId"
                title="Campaign"
                dataIndex="campaignId"
                render={id => (info.campaigns as CampaignBean[]).find(item => id == item.id)?.name}
            />
            <Column
                key="totalBudget"
                title="Total Budget"
                dataIndex="totalBudget"
                render={amount => formatLocale(amount ?? 0, v => '$' + v)}
            />
            <Column
                key="totalCost"
                title="Total Cost"
                dataIndex="totalCost"
                render={amount => formatLocale(amount ?? 0, v => '$' + v)}
            />
            <Column
                key="daysLeft"
                title="Days Left"
                dataIndex="endDate"
                render={endDate => daysToEnd(now, endDate)}
            />
            <Column
                sorter
                key="status"
                title="Status"
                dataIndex="status"
                render={(_text, record: AdGroupBean) => (
                    <Select
                        value={record.status}
                        size="small"
                        className="campaign-status-select"
                        onChange={(value) => changeStatus(record, value)}
                    >
                        {Object.values(AdGroupBean.StatusEnum).map(type => (
                            <Option className="campaign-status-select-item" key={type} value={type}>{capitalizedView(type)}</Option>
                        ))}
                    </Select>
                )}
            />
            <Column
                key="modified"
                title="Last Modified"
                dataIndex="modified"
                sorter
            />
            <Column align="center" key="action" title="Actions" render={(text: string, record: AdGroupBean) => (
                <span>
                    <Link to={`/ad-group/${record.id}/edit`}>
                        <Button type="text" >
                            <FormOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Link to={`/ad-group/create?copyAdGroupId=${record.id}`}>
                        <Button type="text" >
                            <CopyOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            )} />
        </Table>
    </Card>
}

export default AdGroupList


// WEBPACK FOOTER //
// ./src/component/app/adGroup/AdGroupList.tsx